import { RGB, XYZ, LAB } from './types';

/**
 * Convert hex color to RGB values (0-1 range)
 */
export const hexToRgb = (hex: string): RGB => {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  const num = parseInt(hex, 16);
  return [
    ((num >> 16) & 255) / 255,
    ((num >> 8) & 255) / 255,
    (num & 255) / 255
  ];
};

/**
 * Convert RGB to XYZ color space
 */
export const rgbToXyz = ([r, g, b]: RGB): XYZ => {
  r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
  b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;
  
  r *= 100;
  g *= 100;
  b *= 100;
  
  return [
    (r * 0.4124 + g * 0.3576 + b * 0.1805) / 95.047,
    (r * 0.2126 + g * 0.7152 + b * 0.0722) / 100.0,
    (r * 0.0193 + g * 0.1192 + b * 0.9505) / 108.883
  ];
};

/**
 * Convert XYZ to LAB color space
 */
export const xyzToLab = ([x, y, z]: XYZ): LAB => {
  x = x > 0.008856 ? Math.cbrt(x) : (7.787 * x) + (16 / 116);
  y = y > 0.008856 ? Math.cbrt(y) : (7.787 * y) + (16 / 116);
  z = z > 0.008856 ? Math.cbrt(z) : (7.787 * z) + (16 / 116);
  
  return [
    (116 * y) - 16,
    500 * (x - y),
    200 * (y - z)
  ];
};

/**
 * Convert LAB to XYZ color space
 */
export const labToXyz = ([l, a, b]: LAB): XYZ => {
  let y = (l + 16) / 116;
  let x = a / 500 + y;
  let z = y - b / 200;
  
  x = Math.pow(x, 3) > 0.008856 ? Math.pow(x, 3) : (x - 16 / 116) / 7.787;
  y = Math.pow(y, 3) > 0.008856 ? Math.pow(y, 3) : (y - 16 / 116) / 7.787;
  z = Math.pow(z, 3) > 0.008856 ? Math.pow(z, 3) : (z - 16 / 116) / 7.787;
  
  return [x * 95.047, y * 100.0, z * 108.883];
};

/**
 * Convert XYZ to RGB color space
 */
export const xyzToRgb = ([x, y, z]: XYZ): RGB => {
  x /= 100;
  y /= 100;
  z /= 100;
  
  let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
  let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
  let b = x * 0.0557 + y * -0.2040 + z * 1.0570;
  
  r = r > 0.0031308 ? 1.055 * Math.pow(r, 1 / 2.4) - 0.055 : 12.92 * r;
  g = g > 0.0031308 ? 1.055 * Math.pow(g, 1 / 2.4) - 0.055 : 12.92 * g;
  b = b > 0.0031308 ? 1.055 * Math.pow(b, 1 / 2.4) - 0.055 : 12.92 * b;
  
  return [
    Math.min(Math.max(0, r), 1),
    Math.min(Math.max(0, g), 1),
    Math.min(Math.max(0, b), 1)
  ];
};

/**
 * Convert RGB to hex color
 */
export const rgbToHex = ([r, g, b]: RGB): string => {
  const toHex = (n: number) => Math.round(n * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Convert hex color to LAB color space
 */
export const hexToLab = (hex: string): LAB => {
  return xyzToLab(rgbToXyz(hexToRgb(hex)));
};

/**
 * Convert LAB color to hex
 */
export const labToHex = (lab: LAB): string => {
  return rgbToHex(xyzToRgb(labToXyz(lab)));
};